import React from 'react';
import { Buildings, Door, Ruler } from '@phosphor-icons/react';
import type { ObjektZeile } from '../../../../lib/immobilien/portfolio';
import { objektTransitionName } from '../../../../lib/immobilien/viewTransition';
import { BUCHHALTUNG_FOLGT } from '../../../../lib/immobilien/hinweise';
import { fotoHintergrund } from '../../../../lib/immobilien/url';

/**
 * Objektkachel (README §5.1): 320 px hoch, Radius 30, Foto als Hintergrund,
 * Chip mit Objektnummer, Belegungsring (46 px), Glas-Block mit Name/Adresse/Kennzahlen/Saldo.
 */
export function ObjektKachel({ z, onOeffnen }: { z: ObjektZeile; onOeffnen: () => void }) {
  const tastatur = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOeffnen(); }
  };
  return (
    <article
      role="link"
      tabIndex={0}
      aria-label={`${z.name} öffnen`}
      onClick={onOeffnen}
      onKeyDown={tastatur}
      className="immo-hover-kachel relative flex flex-col justify-between overflow-hidden cursor-pointer"
      style={{
        viewTransitionName: objektTransitionName(z.id),
        height: 320, borderRadius: 30, padding: 14,
        background: fotoHintergrund(z.bildUrl) ?? 'var(--fill3)',
        boxShadow: '0 20px 50px var(--shade), inset 0 0 0 1px var(--line)',
      }}
    >
      {!z.bildUrl && (
        <>
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'repeating-linear-gradient(135deg,var(--fill2) 0 12px,transparent 12px 24px)' }} />
          <Buildings weight="duotone" size={40} color="var(--ink3)" className="absolute pointer-events-none" style={{ top: '38%', left: 'calc(50% - 20px)' }} aria-hidden />
        </>
      )}
      <div className="relative flex justify-between items-start">
        {z.nummer ? (
          <span className="immo-zahl flex items-center"
            style={{ height: 26, padding: '0 10px', borderRadius: 13, background: 'var(--chip)', color: 'var(--chip-ink)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', fontSize: 12, fontWeight: 700 }}>
            {z.nummer}{z.archiviert ? ' · Archiviert' : ''}
          </span>
        ) : <span />}
        <div title={z.mietEinheiten > 0 ? `Belegung ${z.mietBelegt} von ${z.mietEinheiten}` : 'Keine Einheit in Vermietung durch uns (WEG ohne SEV)'} className="flex items-center justify-center"
          style={{ width: 46, height: 46, borderRadius: '50%', background: `conic-gradient(var(--acc) ${z.belegungProzent}%, var(--fill3) 0)`, boxShadow: '0 4px 14px var(--shade)' }}>
          <div className="flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--chip)', color: 'var(--chip-ink)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', fontSize: 11, fontWeight: 700 }}>
            {z.mietEinheiten > 0 ? `${z.belegungProzent}%` : '–'}
          </div>
        </div>
      </div>
      <div className="immo-glas relative flex flex-col" style={{ borderRadius: 22, padding: '14px 16px', gap: 10 }}>
        <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
          <span className="truncate" style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>{z.name}</span>
          <span className="truncate" style={{ fontSize: 12, color: 'var(--ink2)' }}>{z.adresse || 'Keine Adresse hinterlegt'}</span>
        </div>
        <div className="flex justify-between items-center" style={{ gap: 8, fontSize: 12 }}>
          <span className="flex whitespace-nowrap" style={{ gap: 12, color: 'var(--ink2)' }}>
            <span className="flex items-center" style={{ gap: 5 }}>
              <Door size={14} color="var(--ink2)" aria-hidden /><b style={{ fontWeight: 600, color: 'var(--ink)' }}>{z.einheiten}</b>
            </span>
            <span className="flex items-center" style={{ gap: 5 }}>
              <Ruler size={14} color="var(--ink2)" aria-hidden />
              <b className="immo-zahl" style={{ fontWeight: 600, color: 'var(--ink)' }}>{Math.round(z.flaeche).toLocaleString('de-DE')}</b> m²
            </span>
          </span>
          {/* Saldo kommt aus dem gemeinsamen Mietkonto – startet mit der Buchhaltung. */}
          <span className="immo-zahl flex items-center" title={`Saldo: ${BUCHHALTUNG_FOLGT}`}
            style={{ height: 24, padding: '0 10px', borderRadius: 12, fontWeight: 700, background: 'var(--fill3)', color: 'var(--ink3)' }}>
            Saldo –
          </span>
        </div>
      </div>
    </article>
  );
}
